import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./lib/auth";
import { Layout, AdminLayout } from "./components/Layout";
import { Toaster } from "./components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";

// Pages
import Home from "./pages/Home";
import Booking from "./pages/Booking";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import CustomerAppointments from "./pages/CustomerAppointments";
import CustomerDocuments from "./pages/CustomerDocuments";
import CustomerPayments from "./pages/CustomerPayments";
import Settings from "./pages/Settings";
import Datenschutz from "./pages/Datenschutz";
import Impressum from "./pages/Impressum";
import AGB from "./pages/AGB";
import SetupWizard from "./pages/SetupWizard";
import AdminContracts from "./pages/admin/AdminContracts";
import AdminContractForm from "./pages/admin/AdminContractForm";
import AdminContractDetail from "./pages/admin/AdminContractDetail";
import SetPassword from "./pages/SetPassword";
import AdminPinGate from "./components/AdminPinGate";
import ForgotPassword from "./pages/ForgotPassword";

// SEO Landing Pages
import SeoLandingPage from "./pages/SeoLandingPage";
import ServicePage from "./pages/ServicePage";
import ServiceCityPage from "./pages/ServiceCityPage";
import ServicesOverview from "./pages/ServicesOverview";
import FaqPage from "./pages/seo/FaqPage";
import SoArbeitenWirPage from "./pages/seo/SoArbeitenWirPage";
import RatgeberHub from "./pages/seo/RatgeberHub";
import { RatgeberWohnungsuebergabe, RatgeberTapeteEntfernen, RatgeberWandfarbe } from "./pages/seo/RatgeberArtikel";
import LeistungenOverview from "./pages/seo/LeistungenOverview";
import LeistungDetail from "./pages/seo/LeistungDetail";
import EinsatzgebietPage from "./pages/seo/EinsatzgebietPage";
import KontaktPage from "./pages/seo/KontaktPage";
import MalerDormagenHub from "./pages/seo/MalerDormagenHub";

// Ratgeber Pages
import RatgeberPage, { RatgeberListPage } from "./pages/RatgeberPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminAppointmentsList from "./pages/admin/AdminAppointmentsList";
import AdminTodayAppointments from "./pages/admin/AdminTodayAppointments";
import AdminAppointments from "./pages/admin/AdminAppointments";
import AdminFiles from "./pages/admin/AdminFiles";
import AdminChats from "./pages/admin/AdminChats";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminDocumentGenerator from "./pages/admin/AdminDocumentGenerator";
import AdminLetters from "./pages/admin/AdminLetters";
import AdminProtocol from "./pages/admin/AdminProtocol";
import AdminEmployees from "./pages/admin/AdminEmployees";
import AdminCustomization from "./pages/admin/AdminCustomization";
import AdminAnleitung from "./pages/admin/AdminAnleitung";
import AdminGutachten from "./pages/admin/AdminGutachten";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminEmailLogs from "./pages/admin/AdminEmailLogs";
import AdminPaymentReminders from "./pages/admin/AdminPaymentReminders";
import AdminEmployeePayments from "./pages/admin/AdminEmployeePayments";
import AdminAufmass from "./pages/admin/AdminAufmass";
import AdminEinsatzplanung from "./pages/admin/AdminEinsatzplanung";
import AdminZeiterfassung from "./pages/admin/AdminZeiterfassung";
import AdminWerkzeug from "./pages/admin/AdminWerkzeug";
import AdminLohnabrechnung from "./pages/admin/AdminLohnabrechnung";
import AdminGrundriss from "./pages/admin/AdminGrundriss";
import AdminDiscountCodes from "./pages/admin/AdminDiscountCodes";
import AdminLicenses from "./pages/admin/AdminLicenses";
import LicenseRegister from "./pages/LicenseRegister";
import LicenseRenew from "./pages/LicenseRenew";
import MitarbeiterStempeluhr from "./pages/MitarbeiterStempeluhr";

// Protected Route Component
function ProtectedRoute({ children, requireAdmin = false, allowSetupBypass = false, allowLicenseInvalid = false }) {
  const { isAuthenticated, isAdmin, loading, user } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Mitarbeiter dürfen NUR ihre Stempeluhr-Seite sehen – sonst nichts.
  if (user?.role === 'employee' && location.pathname !== '/stempeluhr') {
    return <Navigate to="/stempeluhr" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Admin-PIN-Gate: zusätzliche Sicherheitsebene für den Admin-Bereich
  if (requireAdmin && isAdmin) {
    return <AdminPinGate>{children}</AdminPinGate>;
  }
  
  // License invalid (deactivated/deleted/expired): force renew page
  if (user?.license_valid === false && !allowLicenseInvalid && location.pathname !== '/lizenz-erneuern') {
    return <Navigate to="/lizenz-erneuern" replace />;
  }
  
  // Tenant admin first-login: redirect to setup wizard
  const isTenantAdmin = user?.role === 'admin' && user?.tenant_id && user?.tenant_id !== 'main';
  if (isTenantAdmin && user?.setup_completed === false && !allowSetupBypass && location.pathname !== '/admin/setup') {
    return <Navigate to="/admin/setup" replace />;
  }
  
  return children;
}

// Customer Route - redirect admins to admin panel
function CustomerRoute({ children }) {
  const { isAuthenticated, isAdmin, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Mitarbeiter → immer nur Stempeluhr
  if (user?.role === 'employee') {
    return <Navigate to="/stempeluhr" replace />;
  }
  
  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes with Layout */}
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/termin" element={<Layout><Booking /></Layout>} />
      <Route path="/datenschutz" element={<Layout><Datenschutz /></Layout>} />
      <Route path="/impressum" element={<Layout><Impressum /></Layout>} />
      <Route path="/agb" element={<Layout><AGB /></Layout>} />
      
      {/* New SEO/GEO pages - ergänzen die Startseite, überschreiben nichts */}
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/so-arbeiten-wir" element={<SoArbeitenWirPage />} />
      <Route path="/kontakt" element={<KontaktPage />} />
      <Route path="/einsatzgebiet" element={<EinsatzgebietPage />} />
      <Route path="/leistungen" element={<LeistungenOverview />} />
      <Route path="/leistungen/:slug" element={<LeistungDetail />} />
      <Route path="/maler-dormagen" element={<MalerDormagenHub />} />
      <Route path="/maler-koeln" element={<MalerDormagenHub cityOverride="Köln" />} />
      <Route path="/maler-duesseldorf" element={<MalerDormagenHub cityOverride="Düsseldorf" />} />
      <Route path="/ratgeber" element={<RatgeberHub />} />
      <Route path="/ratgeber/wohnungsuebergabe" element={<RatgeberWohnungsuebergabe />} />
      <Route path="/ratgeber/tapete-entfernen" element={<RatgeberTapeteEntfernen />} />
      <Route path="/ratgeber/wandfarbe-auswaehlen" element={<RatgeberWandfarbe />} />
      
      {/* Password Reset Routes */}
      <Route path="/passwort-setzen" element={<SetPassword />} />
      <Route path="/passwort-vergessen" element={<ForgotPassword />} />
      <Route path="/lizenz-registrieren" element={<LicenseRegister />} />
      
      {/* SEO Landing Pages - All Cities and Districts */}
      <Route path="/maler-dormagen" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-dormagen-mitte" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-dormagen-horrem" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-dormagen-hackenbroich" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-dormagen-nievenheim" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-dormagen-zons" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-dormagen-rheinfeld" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-dormagen-delrath" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-dormagen-gohr" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-dormagen-straberg" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-dormagen-delhoven" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-dormagen-stuerzelberg" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-stadtmitte" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-altstadt" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-bilk" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-unterbilk" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-oberbilk" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-friedrichstadt" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-derendorf" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-pempelfort" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-golzheim" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-flingern-nord" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-flingern-sued" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-eller" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-garath" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-benrath" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-kaiserswerth" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-rath" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-heerdt" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-oberkassel" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-loerick" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-holthausen" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-reisholz" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-wersten" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-himmelgeist" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-angermund" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-gerresheim" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-grafenberg" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-moersenbroich" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-duesseltal" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-stockum" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-niederkassel" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-hassels" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-urdenbach" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-vennhausen" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-unterrath" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-lichtenbroich" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-flehe" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-volmerswerth" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-hamm" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-ludenberg" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-hubbelrath" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-knittkuhl" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-itter" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-wittlaer" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-lohausen" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-duesseldorf-carlstadt" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-innenstadt" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-ehrenfeld" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-nippes" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-kalk" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-muelheim" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-porz" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-chorweiler" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-lindenthal" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-rodenkirchen" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-deutz" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-poll" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-suelz" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-klettenberg" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-bayenthal" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-marienburg" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-raderberg" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-raderthal" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-zollstock" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-riehl" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-neuehrenfeld" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-bickendorf" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-ossendorf" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-neustadt-nord" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-neustadt-sued" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-altstadt-nord" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-altstadt-sued" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-braunsfeld" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-junkersdorf" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-loevenich" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-weiden" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-koeln-widdersdorf" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-wiesdorf" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-opladen" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-schlebusch" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-rheindorf" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-kueppersteg" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-hitdorf" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-quettingen" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-buerrig" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-manfort" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-steinbuechel" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-luetzenkirchen" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-leverkusen-bergisch-neukirchen" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-monheim" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-monheim-mitte" element={<Layout><SeoLandingPage /></Layout>} />
      <Route path="/maler-monheim-baumberg" element={<Layout><SeoLandingPage /></Layout>} />
      
      {/* SEO Service Pages */}
      <Route path="/leistungen" element={<Layout><ServicesOverview /></Layout>} />
      <Route path="/leistungen/:serviceSlug" element={<Layout><ServicePage /></Layout>} />
      
      {/* Ratgeber Pages */}
      <Route path="/ratgeber" element={<Layout><RatgeberListPage /></Layout>} />
      <Route path="/ratgeber/:slug" element={<Layout><RatgeberPage /></Layout>} />
      
      {/* Service-City Landing Pages - Düsseldorf */}
      <Route path="/innenanstrich-duesseldorf" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/fassadenarbeiten-duesseldorf" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/tapezierarbeiten-duesseldorf" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/renovierung-duesseldorf" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/wohnungsuebergabe-duesseldorf" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/lackierarbeiten-duesseldorf" element={<Layout><ServiceCityPage /></Layout>} />
      
      {/* Service-City Landing Pages - Dormagen */}
      <Route path="/innenanstrich-dormagen" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/fassadenarbeiten-dormagen" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/tapezierarbeiten-dormagen" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/renovierung-dormagen" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/wohnungsuebergabe-dormagen" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/lackierarbeiten-dormagen" element={<Layout><ServiceCityPage /></Layout>} />
      
      {/* Service-City Landing Pages - Köln */}
      <Route path="/innenanstrich-koeln" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/fassadenarbeiten-koeln" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/tapezierarbeiten-koeln" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/renovierung-koeln" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/wohnungsuebergabe-koeln" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/lackierarbeiten-koeln" element={<Layout><ServiceCityPage /></Layout>} />
      
      {/* Service-City Landing Pages - Leverkusen */}
      <Route path="/innenanstrich-leverkusen" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/fassadenarbeiten-leverkusen" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/tapezierarbeiten-leverkusen" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/renovierung-leverkusen" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/wohnungsuebergabe-leverkusen" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/lackierarbeiten-leverkusen" element={<Layout><ServiceCityPage /></Layout>} />
      
      {/* Service-City Landing Pages - Monheim */}
      <Route path="/innenanstrich-monheim" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/fassadenarbeiten-monheim" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/tapezierarbeiten-monheim" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/renovierung-monheim" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/wohnungsuebergabe-monheim" element={<Layout><ServiceCityPage /></Layout>} />
      <Route path="/lackierarbeiten-monheim" element={<Layout><ServiceCityPage /></Layout>} />
      
      {/* Auth Routes - No Layout */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      
      {/* Customer Protected Routes */}
      <Route path="/dashboard" element={
        <CustomerRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </CustomerRoute>
      }>
        <Route path="termine" element={<CustomerAppointments />} />
        <Route path="dokumente" element={<CustomerDocuments />} />
        <Route path="zahlungen" element={<CustomerPayments />} />
      </Route>
      
      <Route path="/dashboard/termine" element={
        <CustomerRoute>
          <Layout>
            <CustomerAppointments />
          </Layout>
        </CustomerRoute>
      } />
      
      <Route path="/dashboard/dokumente" element={
        <CustomerRoute>
          <Layout>
            <CustomerDocuments />
          </Layout>
        </CustomerRoute>
      } />
      
      <Route path="/dashboard/zahlungen" element={
        <CustomerRoute>
          <Layout>
            <CustomerPayments />
          </Layout>
        </CustomerRoute>
      } />
      
      {/* Settings - Both customer and admin */}
      <Route path="/einstellungen" element={
        <ProtectedRoute>
          <Layout>
            <Settings />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/lizenz-erneuern" element={
        <ProtectedRoute allowLicenseInvalid allowSetupBypass>
          <LicenseRenew />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/setup" element={
        <ProtectedRoute requireAdmin allowSetupBypass>
          <SetupWizard />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminDashboard />
          </AdminLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/vertraege" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout><AdminContracts /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/vertraege/neu" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout><AdminContractForm /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/vertraege/:id" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout><AdminContractDetail /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/vertraege/:id/bearbeiten" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout><AdminContractForm /></AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/kunden" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminCustomers />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/termine-liste" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminAppointmentsList />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/termine-heute" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminTodayAppointments />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/termine" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminAppointments />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/dateien" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminFiles />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/chats" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminChats />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/einstellungen" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminSettings />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/dokumente" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminDocumentGenerator />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/briefe" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminLetters />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/protokolle" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminProtocol />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/mitarbeiter" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminEmployees />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/anpassungen" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminCustomization />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/anleitung" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminAnleitung />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/gutachten" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminGutachten />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/auftraege" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminLeads />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/postausgang" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminEmailLogs />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/zahlungserinnerungen" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminPaymentReminders />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/mitarbeiter-zahlungen" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminEmployeePayments />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/aufmass" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminAufmass />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/einsatzplanung" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminEinsatzplanung />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/zeiterfassung" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminZeiterfassung />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/werkzeug" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminWerkzeug />
          </AdminLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/grundriss" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminGrundriss />
          </AdminLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/lohnabrechnung" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminLohnabrechnung />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/rabattcodes" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminDiscountCodes />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/lizenzen" element={
        <ProtectedRoute requireAdmin>
          <AdminLayout>
            <AdminLicenses />
          </AdminLayout>
        </ProtectedRoute>
      } />
      
      {/* Employee Time Clock - No admin layout, standalone page */}
      <Route path="/stempeluhr" element={
        <ProtectedRoute>
          <MitarbeiterStempeluhr />
        </ProtectedRoute>
      } />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
